import moment from "moment";
import { axiosGet } from "../apis/useAxios";

/**
 * UserActivityLog.LogType values (API Models/FixeCode.UserActivityLogType): which entity a history row belongs to.
 * ColumnValue1 / ColumnValue2 hold the record key (id or code; a second key for config tables).
 */
export const LOG_TYPES = {
  airport: "Airport",
  hotel: "Hotel",
  activityLocation: "ActivityLocation",
  transport: "Transport",
  activityCode: "ActivityCode",
  activityType: "ActivityType",
  reservation: "Reservation",
  user: "User",
  staffAddress: "StaffAddress",
  airlineSetting: "AirlineSetting",
  appMenu: "AppMenu",
  hotelPriority: "HotelPriority",
  rating: "Rating",
  feedback: "Feedback",
};

/** Screen names of the log types, for a user's activity list. */
export const LOG_TYPE_LABELS = {
  Airport: "Airport",
  Hotel: "Hotel",
  ActivityLocation: "Activity Location",
  ActivityCode: "Activity Code",
  ActivityType: "Activity Type",
  Reservation: "Reservation",
  User: "User",
  StaffAddress: "Staff Address",
  Transport: "Local Vendor",
  AirlineSetting: "Airline Setting",
  AppMenu: "App Menu",
  UserAccess: "Access rights",
  EmailSetting: "Email Setting",
  HotelPriority: "Booking Hotel Priority",
  Rating: "Rating",
  Feedback: "Feedback",
};

/**
 * History rows, newest first: GET UserActivityLog/list.
 * - one record: logType + columnValue1 (+ columnValue2);
 * - everything one user did, on every screen: userName (the logged-in name stored on each row).
 * Resolves to [{ id, logType, columnValue1, columnValue2, description, userName, createdTime }]; "no record" is [].
 */
export async function getActivityLog({ logType, columnValue1, columnValue2 = "", userName }, options) {
  const params = {
    airlineCode: sessionStorage.getItem("session_airlineCode"),
    logType,
    columnValue1,
    columnValue2,
  };
  if (userName) params.userName = userName;
  const response = await axiosGet("UserActivityLog/list", params, options);
  if (response && response.isSuccess === false && !/no record/i.test(response.message ?? "")) {
    throw new Error(response.message || "Unable to load the history.");
  }
  return response?.payload ?? [];
}

/**
 * Splits a history description into its changes. Entries are written as "... [ value ]" and joined
 * with ", " (API DataLayer/ActivityLogText), so split only after "]" to keep commas inside values.
 */
export function splitChanges(description) {
  // No regex lookbehind: older Safari cannot parse it.
  const parts = String(description ?? "").split(/\],\s+/);
  return parts
    // Older rows start with ", , " (empty entries of the previous history text): drop them.
    .map((part, i) => (i < parts.length - 1 ? `${part}]` : part).replace(/^[\s,]+/, "").trim())
    .filter(Boolean);
}

const parseLogTime = (value) => {
  const time = moment(value, ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true);
  return time.isValid() ? time : null;
};

/** A history row's time as DD/MM/YYYY HH:mm (the text as it is when it cannot be read). */
export const formatLogTime = (value) => {
  const time = parseLogTime(value);
  return time ? time.format("DD/MM/YYYY HH:mm") : value || "-";
};

/** Screen name of a history row's log type. */
export const logTypeLabel = (logType) => LOG_TYPE_LABELS[logType] || logType || "Record";

/** Search a history opens with: the last 30 days (today - 30 to today), no text. */
export const defaultHistoryFilters = () => ({
  text: "",
  from: moment().subtract(30, "days").format("YYYY-MM-DD"),
  to: moment().format("YYYY-MM-DD"),
});

/**
 * History rows matching a search: text (case-insensitive) in the user, the change text, the screen, the record keys
 * or the shown date; from / to (yyyy-MM-dd, inclusive) on the log date. Empty criteria match everything.
 */
export function filterActivityLog(rows, { text = "", from = "", to = "" } = {}) {
  const needle = text.trim().toLowerCase();
  return (rows ?? []).filter((row) => {
    if (needle) {
      const haystack = [
        row.userName,
        row.description,
        logTypeLabel(row.logType),
        row.columnValue1,
        row.columnValue2,
        formatLogTime(row.createdTime),
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    if (from || to) {
      const time = parseLogTime(row.createdTime);
      if (!time) return false;
      const day = time.format("YYYY-MM-DD");
      if ((from && day < from) || (to && day > to)) return false;
    }
    return true;
  });
}

/**
 * History rows as Excel rows: Date, User, (Screen, Record when withRecord), Changes (one change per line).
 */
export function toActivityLogExportRows(rows, { withRecord = false } = {}) {
  return (rows ?? []).map((row) => ({
    Date: formatLogTime(row.createdTime),
    User: row.userName || "",
    ...(withRecord
      ? { Screen: logTypeLabel(row.logType), Record: [row.columnValue1, row.columnValue2].filter(Boolean).join(" / ") }
      : {}),
    Changes: splitChanges(row.description).join("\n"),
  }));
}
